/**
 * ページの「データ取得が終わった」合図（シグナル）を出すための共通仕組み。
 *
 * これまでページ遷移の白フェードは「DOM の動きが止まったら読み込み完了」と
 * 推測していた。推測は外れることがあり（遅れて走る取得・ポーリングなど）、
 * 白が消えた後に中身が動いて見えることがあった。
 *
 * そこで、実際のデータ取得（Supabase への通信）そのものを監視し、
 * 「今いくつの取得が動いているか（pending）」を数える。これが 0 に戻った瞬間が
 * “そのページのデータ取得が終わった瞬間”になる。ページごとに完了フラグを
 * 立てて回る必要はなく、全ページで自動的に正しい合図が出る。
 *
 * - 監視対象は Supabase ドメインへの fetch（DB・認証・Storage・Edge Functions）。
 * - 画像やアセットなど他ドメインの通信は含めない（読み込みの体感と相関が薄く、
 *   合図を不必要に遅らせるため）。
 * - fetch のラップはアプリ全体でたった1回だけ行う（二重ラップ防止）。
 */

type Listener = () => void;

/** fetch の引数型を環境非依存で導出する（RequestInfo 等のグローバル型名に依存しない） */
type FetchInput = Parameters<typeof fetch>[0];
type FetchInit = Parameters<typeof fetch>[1];

export interface PageLoadState {
  /** 現在処理中のデータ取得（通信）数。0 なら「取得は落ち着いている」 */
  pending: number;
  /** このページ読み込みで、これまでに1件でもデータ取得が走ったか */
  started: boolean;
  /**
   * 遅延読み込み（コード分割）したページの JS チャンク取得中件数。
   * 0 なら「新ページのコードは読み込み済み」。
   * 通信が遅い環境ではチャンク取得そのものに時間がかかり、その間はまだページの
   * 中身が描画されず Supabase へのデータ取得も始まらない。この件数を監視対象に
   * 含めることで、ページ遷移の白フェードがコンテンツより先に明けてしまうのを防ぐ。
   */
  routePending: number;
}

const state: PageLoadState = { pending: 0, started: false, routePending: 0 };
const listeners = new Set<Listener>();

function emit(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // 個々のリスナーの失敗で他のリスナーや本体を止めない
    }
  });
}

/** 現在の状態を読み取る（購読リスナー内から参照する用） */
export function getPageLoadState(): Readonly<PageLoadState> {
  return state;
}

/** 状態変化（取得の開始・終了・リセット）を購読する。解除関数を返す。 */
export function subscribePageLoad(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * ページ移動時に呼ぶ。前のページから残った通信カウントを引き継がないよう、
 * 合図の状態を初期化する。
 */
export function resetPageLoad(): void {
  state.pending = 0;
  state.started = false;
  // 遅延チャンク（routePending）はリセットしない。ナビゲーション時、新しいページの
  // チャンク取得は React の描画フェーズでこのリセットより先に始まっていることがあり、
  // ここで 0 に戻すと「まだ読み込み中なのに完了扱い」になってしまうため。
  emit();
}

/** データ取得の開始を記録する */
export function beginDataRequest(): void {
  state.pending += 1;
  state.started = true;
  emit();
}

/** データ取得の終了（成功・失敗いずれでも）を記録する */
export function endDataRequest(): void {
  state.pending = Math.max(0, state.pending - 1);
  emit();
}

/**
 * 遅延読み込みページの JS チャンク取得の開始を記録する。
 * 取得が終わるまで pending が 0 に戻らないため、フェードはコンテンツの到着を待つ。
 */
export function beginRouteLoad(): void {
  state.routePending += 1;
  emit();
}

/** 遅延読み込みページの JS チャンク取得の終了（成功・失敗いずれでも）を記録する */
export function endRouteLoad(): void {
  state.routePending = Math.max(0, state.routePending - 1);
  emit();
}

/** 監視対象にする Supabase のオリジン（例: https://xxxx.supabase.co） */
function resolveSupabaseOrigin(): string {
  const url = (import.meta.env.VITE_PUBLIC_SUPABASE_URL as string | undefined) ?? '';
  if (!url) return '';
  try {
    return new URL(url).origin;
  } catch {
    return '';
  }
}

const supabaseOrigin = resolveSupabaseOrigin();

function resolveRequestUrl(input: FetchInput): string {
  if (typeof input === 'string') return input;
  if (typeof URL !== 'undefined' && input instanceof URL) return input.href;
  if (input && typeof input === 'object' && 'url' in input) {
    return (input as { url: string }).url;
  }
  return '';
}

interface WindowWithFlag extends Window {
  __pageLoadFetchPatched?: boolean;
}

/**
 * fetch をラップし、Supabase への通信を数える。アプリ全体で1回だけ実行する。
 */
export function installPageLoadTracking(): void {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;

  const win = window as WindowWithFlag;
  if (win.__pageLoadFetchPatched) return;
  win.__pageLoadFetchPatched = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = (input: FetchInput, init?: FetchInit): Promise<Response> => {
    const url = resolveRequestUrl(input);
    const tracked = supabaseOrigin !== '' && url.startsWith(supabaseOrigin);

    if (tracked) beginDataRequest();

    const result = originalFetch(input, init);

    if (tracked) {
      // 成功・失敗どちらでも必ず終了を記録する（失敗で pending が残らないように）
      result.then(
        () => endDataRequest(),
        () => endDataRequest(),
      );
    }

    return result;
  };
}

// このモジュールが読み込まれた時点（＝アプリ起動時）で監視を有効化する
installPageLoadTracking();